"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/lib/utils";
import AQMark from "@/components/atoms/AQMark";
import type { FooterProps } from "@/components/layout/Footer/Footer.props";
import { SocialIcon } from "@/components/atoms/SocialIcon";
import { InlineRichText } from "@/components/atoms/RichText";
import { FooterNewsletter } from "./layout/Footer/FooterNewsletter";
import { Link } from "@/components/atoms/Link";
import { GSAContractHolderLogo } from "@/components/atoms/GSAContractHolderLogo";
import { Text } from "@/components/atoms/Text";
import { Img } from "@/components/atoms/Img";
const FooterLinkSection = t0 => {
  const $ = _c(10);
  const {
    title,
    titleHref,
    links
  } = t0;
  let t1;
  if ($[0] !== title || $[1] !== titleHref) {
    t1 = titleHref ? <Link href={titleHref} className="hover:text-white transition-colors">{title}</Link> : title;
    $[0] = title;
    $[1] = titleHref;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <Text as="p" className="text-primary text-sm font-bold tracking-ultra-wide uppercase">{t1}</Text>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== links) {
    t3 = links.length > 0 && <ul className="space-y-4">{links.map(_temp)}</ul>;
    $[5] = links;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

/**
 * Footer25 Component
 *
 * A comprehensive footer with refined brand interactions.
 *
 * @component
 * @param {FooterProps} props - The properties for the footer.
 */
const Footer25 = t0 => {
  const $ = _c(71);
  const {
    className,
    sections,
    socialLinks,
    copyright,
    address,
    phoneNumber,
    email,
    corporateCredentials,
    naicsCodes
  } = t0;
  let t1;
  if ($[0] !== phoneNumber) {
    t1 = phoneNumber ? phoneNumber.replace(/[^\d+]/g, "") : "";
    $[0] = phoneNumber;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const phoneHref = t1;
  const t2 = copyright || "AgencyQ. All rights reserved.";
  let t3;
  if ($[2] !== t2) {
    t3 = t2.split("|").map(_temp2).filter(Boolean);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let copyrightMain;
  let copyrightRest;
  if ($[4] !== t3) {
    [copyrightMain, ...copyrightRest] = t3;
    $[4] = t3;
    $[5] = copyrightMain;
    $[6] = copyrightRest;
  } else {
    copyrightMain = $[5];
    copyrightRest = $[6];
  }
  let t4;
  if ($[7] !== className) {
    t4 = cn("relative overflow-hidden bg-aq-midnight isolate", className);
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FooterNewsletter />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mb-6 flex items-center gap-4"><AQMark className="h-16 w-16" aria-label="AgencyQ Logo" role="img" /></div>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== address) {
    t7 = address && <address className="not-italic leading-relaxed"><InlineRichText content={address} variant="address" /></address>;
    $[11] = address;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== phoneHref || $[14] !== phoneNumber) {
    t8 = phoneNumber && <a className="hover:text-primary transition-colors" href={`tel:${phoneHref}`} aria-label={`Call ${phoneNumber}`}>{phoneNumber}</a>;
    $[13] = phoneHref;
    $[14] = phoneNumber;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== email) {
    t9 = email && <a className="hover:text-primary transition-colors" href={`mailto:${email}`} aria-label={`Email ${email}`}>{email}</a>;
    $[16] = email;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <div className="flex flex-col gap-2">{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7) {
    t11 = <div className="space-y-4 text-white/70">{t7}{t10}</div>;
    $[21] = t10;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== socialLinks) {
    t12 = socialLinks?.map(_temp3);
    $[24] = socialLinks;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = <div className="flex gap-4 mt-8">{t12}</div>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t13) {
    t14 = <div className="lg:col-span-1 border-r border-white/5 pr-8 last:border-0 md:border-0 lg:border-r">{t6}{t11}{t13}</div>;
    $[28] = t11;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== sections) {
    t15 = sections?.map(_temp5);
    $[31] = sections;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">{t14}{t15}</div>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Text as="p" className="text-primary text-xs font-bold tracking-ultra-wide uppercase">Corporate Credentials</Text>;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== corporateCredentials) {
    t18 = corporateCredentials?.map(_temp6);
    $[37] = corporateCredentials;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="mt-3"><GSAContractHolderLogo /></div>;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t18) {
    t20 = <div className="space-y-6">{t17}<div className="flex flex-col gap-3">{t18}{t19}</div></div>;
    $[40] = t18;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Text as="p" className="text-primary text-xs font-bold tracking-ultra-wide uppercase">NAICS Codes</Text>;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== naicsCodes) {
    t22 = naicsCodes?.map(_temp7);
    $[43] = naicsCodes;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t22) {
    t23 = <div className="space-y-6">{t21}<div className="flex flex-col gap-3">{t22}</div></div>;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t20 || $[48] !== t23) {
    t24 = <div className="mt-16 pt-12 border-t border-white/10"><div className="grid grid-cols-1 md:grid-cols-2 gap-12">{t20}{t23}</div></div>;
    $[47] = t20;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = new Date().getFullYear();
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== copyrightMain) {
    t26 = <p className="text-xs font-semibold tracking-wide text-white/70">© {t25} {copyrightMain}</p>;
    $[51] = copyrightMain;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== copyrightRest) {
    t27 = copyrightRest.map(_temp8);
    $[53] = copyrightRest;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t26 || $[56] !== t27) {
    t28 = <div className="space-y-1.5">{t26}{t27}</div>;
    $[55] = t26;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  let t30;
  let t31;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Link href="/privacy-policy" className="text-xs text-white/70 transition-colors hover:text-primary">Privacy Policy</Link>;
    t30 = <Link href="/terms-of-service" className="text-xs text-white/70 transition-colors hover:text-primary">Terms of Service</Link>;
    t31 = <Link href="/your-privacy-choices#cookie-settings" className="text-xs text-white/70 transition-colors hover:text-primary">Cookie Settings</Link>;
    $[58] = t29;
    $[59] = t30;
    $[60] = t31;
  } else {
    t29 = $[58];
    t30 = $[59];
    t31 = $[60];
  }
  let t32;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-6 gap-y-3 xl:justify-end">{t29}{t30}{t31}<Link href="/your-privacy-choices" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-white/80 transition-colors hover:border-primary hover:text-primary"><Img src="/images/privacy-options.svg" alt="California Consumer Privacy Act (CCPA) Opt-Out Icon" width={30} height={14} className="h-3.5 w-auto" />Your Privacy Choices</Link></nav>;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t28) {
    t33 = <div className="mt-16 flex flex-col gap-6 border-t border-white/10 pt-8 xl:flex-row xl:items-center xl:justify-between xl:gap-x-10">{t28}{t32}</div>;
    $[62] = t28;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== t16 || $[65] !== t24 || $[66] !== t33) {
    t34 = <div className="footer-gradient-background"><div className="container relative z-10 py-4 md:py-16"><div className="mx-auto max-w-7xl p-8 md:p-16">{t16}{t24}{t33}</div></div></div>;
    $[64] = t16;
    $[65] = t24;
    $[66] = t33;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== t34 || $[69] !== t4) {
    t35 = <footer role="contentinfo" aria-label="Site footer" className={t4}>{t5}{t34}</footer>;
    $[68] = t34;
    $[69] = t4;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  return t35;
};
export { Footer25 };
function _temp(link, index) {
  return <li key={index}><a href={link.href} aria-label={link.name} className="group relative inline-block text-white/60 transition-colors hover:text-white">{link.name}<span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full shadow-[var(--aq-shadow-glow-orange-sm)]" /></a></li>;
}
function _temp2(part) {
  return part.trim();
}
function _temp3(social) {
  return <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="group relative w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/20 hover:border-transparent transition-all duration-300 shadow-sm overflow-hidden isolate" aria-label={social.label || social.icon}><div className="absolute inset-0 -z-10 bg-gradient-to-br from-[var(--color-aq-orange)] via-[var(--color-aq-orange)] to-[var(--color-aq-electric-blue)] opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100" /><div className="absolute inset-0 -z-10 bg-gradient-to-br from-[var(--color-aq-orange)] via-[var(--color-aq-orange)] to-[var(--color-aq-electric-blue)] opacity-0 transition-transform duration-500 ease-out group-hover:opacity-100 group-hover:rotate-180 group-hover:scale-150" /><SocialIcon name={social.icon} className="w-5 h-5 relative z-10 transition-colors duration-300 group-hover:text-white" colored={true} /></a>;
}
function _temp4(stacked, sIdx) {
  return <FooterLinkSection key={sIdx} title={stacked.title} titleHref={stacked.titleHref} links={stacked.links} />;
}
function _temp5(menu, idx) {
  return <div key={idx} className="space-y-6"><FooterLinkSection title={menu.title} titleHref={menu.titleHref} links={menu.links} />{menu.stackedSections?.map(_temp4)}</div>;
}
function _temp6(credential) {
  return <div key={credential.code} className="text-sm inline-flex items-center w-fit"><span className="text-white font-medium">{credential.type}</span><span className="text-primary mx-2">/</span><span className="text-white/60">{credential.code}</span></div>;
}
function _temp7(naics) {
  return <div key={naics.code} className="text-sm inline-flex items-center w-fit"><span className="text-white font-bold">{naics.code}</span><span className="text-primary mx-2">/</span><span className="text-white/60">{naics.description}</span></div>;
}
function _temp8(line) {
  return <p key={line} className="text-xs tracking-wide text-white/50">{line}</p>;
}